import { createContext, useContext, useRef } from "react";

import { useStore } from "zustand";

import {
  type GroupByState,
  type GroupByStore,
  type GroupByStoreApi,
  createGroupByStore
} from "./groupByStore";

export const GroupByStoreContext = createContext<Maybe<GroupByStoreApi>>(null);

interface GroupByStoreProviderProps {
  readonly children: React.ReactNode;
  readonly overrides?: Partial<GroupByState>;
}

function GroupByStoreProvider({ children, overrides }: GroupByStoreProviderProps) {
  const storeRef = useRef<Maybe<GroupByStoreApi>>(null);
  if (storeRef.current === null) {
    storeRef.current = createGroupByStore(overrides);
  }
  return (
    <GroupByStoreContext.Provider value={storeRef.current}>
      {children}
    </GroupByStoreContext.Provider>
  );
}

/**
 * Hook to select state from the nearest GroupByStore.
 *
 * Usage (in a Container or Logic Hook — never in a Presenter):
 *   const filterText = useGroupByStore(selectFilterText);
 *
 * For compound selectors that return new object literals (e.g. selectActions,
 * selectColumnFilterDeps), wrap the selector with `useShallow` at the call
 * site to avoid re-renders on every store update:
 *
 *   import { useShallow } from "zustand/react/shallow";
 *   const actions = useGroupByStore(useShallow(selectActions));
 *   const deps = useGroupByStore(useShallow(selectColumnFilterDeps));
 */
function useGroupByStore<T>(selector: (state: GroupByStore) => T): T {
  const store = useContext(GroupByStoreContext);
  if (!store) {
    throw new Error("useGroupByStore must be used within a <GroupByStoreProvider>");
  }
  return useStore(store, selector);
}

export { GroupByStoreProvider, useGroupByStore };
